import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type RoleRow = {
  id: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
  _count?: {
    userRoles: number;
    rolePermissions: number;
  };
};

export default function RolesPage() {
  const { authorizedFetch } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/rbac/roles`);
      if (!res.ok) throw new Error(t('backoffice.roles_load_error', 'Unable to load roles.'));
      const data = (await res.json()) as RoleRow[];
      setRoles(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('backoffice.roles_load_error', 'Unable to load roles.'));
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/rbac/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description: description || undefined }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || t('backoffice.roles_create_error', 'Unable to create role.'));
      }
      setName('');
      setDescription('');
      setShowCreate(false);
      await load();
    } catch (e2) {
      setError(e2 instanceof Error ? e2.message : t('backoffice.roles_create_error', 'Unable to create role.'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={location.pathname}
    >
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.roles_title', 'Roles & permissions')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('backoffice.roles_subtitle', 'Create roles and assign fine-grained permissions for each feature.')}
          </p>
        </section>

        {error && <p className="text-xs text-red-500">{error}</p>}

        <section className="workspace-panel">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-slate-900">
              {t('backoffice.roles_list_title', 'Roles')}
            </h2>
            <button
              type="button"
              onClick={() => setShowCreate((v) => !v)}
              className="rounded-full bg-brand-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-brand-500"
            >
              {showCreate
                ? t('common.cancel', 'Cancel')
                : t('backoffice.roles_new', 'New role')}
            </button>
          </div>

          {showCreate && (
            <form onSubmit={handleCreate} className="mb-6 grid gap-3 border-b border-slate-100 pb-5 text-xs md:grid-cols-3">
              <div className="md:col-span-1 space-y-1">
                <label className="block font-medium text-slate-700">
                  {t('backoffice.roles_field_name', 'Role name')}
                  <span className="ml-0.5 text-red-500">*</span>
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('backoffice.roles_name_placeholder', 'e.g. ACCOUNTANT')}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-400/70"
                />
              </div>
              <div className="md:col-span-2 space-y-1">
                <label className="block font-medium text-slate-700">
                  {t('backoffice.roles_field_description', 'Description')}
                </label>
                <input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('backoffice.roles_description_placeholder', 'Short description')}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-400/70"
                />
              </div>
              <div className="md:col-span-3 flex justify-end">
                <button
                  type="submit"
                  disabled={creating || !name.trim()}
                  className="rounded-md bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {creating ? t('common.saving', 'Saving…') : t('backoffice.roles_create', 'Create role')}
                </button>
              </div>
            </form>
          )}

          {loading ? (
            <p className="text-sm text-slate-600">{t('common.loading', 'Loading…')}</p>
          ) : roles.length === 0 ? (
            <p className="text-sm text-slate-600">{t('backoffice.roles_empty', 'No roles yet.')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-xs sm:text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-2 pr-4">{t('backoffice.roles_col_name', 'Name')}</th>
                    <th className="py-2 pr-4">{t('backoffice.roles_col_description', 'Description')}</th>
                    <th className="py-2 pr-4">{t('backoffice.roles_col_permissions', 'Permissions')}</th>
                    <th className="py-2 pr-4">{t('backoffice.roles_col_users', 'Users')}</th>
                    <th className="py-2 pr-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {roles.map((r) => (
                    <tr key={r.id}>
                      <td className="py-2 pr-4 font-semibold text-slate-900">
                        {r.name}{' '}
                        {r.isSystem && (
                          <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                            {t('backoffice.roles_badge_system', 'System')}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-slate-700">
                        {r.description || '—'}
                      </td>
                      <td className="py-2 pr-4 text-slate-700">
                        {r._count?.rolePermissions ?? '—'}
                      </td>
                      <td className="py-2 pr-4 text-slate-700">
                        {r._count?.userRoles ?? '—'}
                      </td>
                      <td className="py-2 pr-2 text-right">
                        <button
                          type="button"
                          onClick={() => navigate(`/workspace/roles/${r.id}`)}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 hover:border-slate-400"
                        >
                          {t('backoffice.roles_manage', 'Manage')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </MainLayout>
  );
}

